import { useState } from 'react';
import { useForm } from 'react-final-form';
import { isEqual } from 'lodash';

import { BUTTON_STATUS, BUTTON_STATUS_TEXTS } from '@funnels/constants';
import { useDidUpdate } from '@funnels/hooks';

import { useFormValues } from '../useFormValues';
import { usePrevious } from '../usePrevious';
import { useSelector } from 'react-redux';
import { ReduxStoreData } from '@funnels/types';

export function useButtonStatusAndText(isLoading: boolean, isError: boolean) {
  const form = useForm();
  const isDirty = form.getState().dirty;
  const [formValues] = useFormValues();
  const prevFormValues = usePrevious(formValues);

  const applicationLockIsActive = useSelector(({ applications: { locked } }: ReduxStoreData) => locked);

  const initialButtonStatus = applicationLockIsActive ? BUTTON_STATUS.APPLICATION_LOCKED : BUTTON_STATUS.PRISTINE;
  const initialButtonText = applicationLockIsActive
    ? BUTTON_STATUS_TEXTS.APPLICATION_LOCKED
    : BUTTON_STATUS_TEXTS.PRISTINE;

  // In some TV form will still be dirty after submit so need another flag here.
  const isFormValuesChanged = !isEqual(prevFormValues, formValues);
  const [isSaving, setIsSaving] = useState(false);
  const [buttonStatus, setButtonStatus] = useState(initialButtonStatus);
  const [buttonStatusText, setButtonStatusText] = useState(initialButtonText);
  const [isDisabled, setIsDisabled] = useState(true);

  useDidUpdate(() => {
    if (applicationLockIsActive) {
      setIsSaving(false);
      setButtonStatus(BUTTON_STATUS.APPLICATION_LOCKED);
      setButtonStatusText(BUTTON_STATUS_TEXTS.APPLICATION_LOCKED);
      setIsDisabled(true);
    } else if (isSaving && !isLoading && !isError) {
      setIsSaving(false);
      setButtonStatus(BUTTON_STATUS.SAVING_SUCCESSFUL);
      setButtonStatusText(BUTTON_STATUS_TEXTS.SAVING_SUCCESSFUL);
      setIsDisabled(true);
    } else if (isError) {
      setIsSaving(false);
      setButtonStatus(BUTTON_STATUS.SAVING_UNSUCCESSFUL);
      setButtonStatusText(BUTTON_STATUS_TEXTS.SAVING_UNSUCCESSFUL);
      setIsDisabled(false);
    } else if (
      ((isDirty || isFormValuesChanged) && buttonStatus === BUTTON_STATUS.PRISTINE) ||
      (isFormValuesChanged && buttonStatus === BUTTON_STATUS.SAVING_SUCCESSFUL)
    ) {
      setButtonStatus(BUTTON_STATUS.DIRTY);
      setButtonStatusText(BUTTON_STATUS_TEXTS.DIRTY);
      setIsDisabled(false);
    } else if (isLoading) {
      setIsSaving(true);
      setButtonStatus(BUTTON_STATUS.SAVING);
      setButtonStatusText(BUTTON_STATUS_TEXTS.SAVING);
      setIsDisabled(true);
    }
  }, [isLoading, isError, isSaving, isFormValuesChanged, isDirty, applicationLockIsActive]);

  return { buttonStatus, buttonStatusText, isDisabled };
}
